import { supabase } from '@/lib/supabase/client'
import type {
  NDTMethod,
  NDTTestProcedure,
  NDTTestAcceptance,
  CompanyNDTSettings,
  CompanyNDTSettingsDetailed,
  CompanyNDTSettingsUpdate
} from '@/types/ndtSettings.types'

/**
 * Gets all test procedures for a specific NDT method
 */
export async function getNDTTestProcedures(ndtMethod: NDTMethod): Promise<NDTTestProcedure[]> {
  try {
    const { data, error } = await supabase
      .from('ndt_test_procedures')
      .select('*')
      .eq('ndt_method', ndtMethod)
      .order('display_order')

    if (error) {
      console.error('Error fetching NDT test procedures:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Failed to fetch NDT test procedures:', error)
    return []
  }
}

/**
 * Gets the default test procedure for a specific NDT method
 */
export async function getDefaultNDTTestProcedure(ndtMethod: NDTMethod): Promise<NDTTestProcedure | null> {
  try {
    const { data, error } = await supabase
      .from('ndt_test_procedures')
      .select('*')
      .eq('ndt_method', ndtMethod)
      .eq('is_default', true)
      .single()

    if (error) {
      console.error('Error fetching default NDT test procedure:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Failed to fetch default NDT test procedure:', error)
    return null
  }
}

/**
 * Gets all test acceptance criteria for a specific NDT method
 */
export async function getNDTTestAcceptance(ndtMethod: NDTMethod, procedureId?: string): Promise<NDTTestAcceptance[]> {
  try {
    let query = supabase
      .from('ndt_test_acceptance')
      .select('*')
      .eq('ndt_method', ndtMethod)

    if (procedureId) {
      query = query.eq('procedure_id', procedureId)
    }

    const { data, error } = await query.order('display_order')

    if (error) {
      console.error('Error fetching NDT test acceptance:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Failed to fetch NDT test acceptance:', error)
    return []
  }
}

/**
 * Gets the default test acceptance for a specific NDT method and procedure
 */
export async function getDefaultNDTTestAcceptance(
  ndtMethod: NDTMethod,
  procedureId?: string
): Promise<NDTTestAcceptance | null> {
  try {
    let query = supabase
      .from('ndt_test_acceptance')
      .select('*')
      .eq('ndt_method', ndtMethod)
      .eq('is_default', true)

    if (procedureId) {
      query = query.eq('procedure_id', procedureId)
    }

    const { data, error } = await query.single()

    if (error) {
      console.error('Error fetching default NDT test acceptance:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Failed to fetch default NDT test acceptance:', error)
    return null
  }
}

/**
 * Fetches NDT settings for a specific company and method
 */
export async function getCompanyNDTSettings(
  companyId: string,
  ndtMethod: NDTMethod
): Promise<CompanyNDTSettingsDetailed | null> {
  try {
    const { data, error } = await supabase
      .from('company_ndt_settings_detailed')
      .select('*')
      .eq('company_id', companyId)
      .eq('ndt_method', ndtMethod)
      .single()

    if (error) {
      console.error('Error fetching company NDT settings:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Failed to fetch company NDT settings:', error)
    return null
  }
}

/**
 * Gets default NDT settings (used when no company is selected or no settings exist)
 */
export async function getDefaultNDTSettings(ndtMethod: NDTMethod): Promise<Partial<CompanyNDTSettings>> {
  const defaultProcedure = await getDefaultNDTTestProcedure(ndtMethod)
  const defaultAcceptance = await getDefaultNDTTestAcceptance(ndtMethod, defaultProcedure?.id)

  return {
    ndt_method: ndtMethod,
    default_procedure_id: defaultProcedure?.id || null,
    default_acceptance_id: defaultAcceptance?.id || null,
    default_reference_standards: '---',
    custom_fields: {
      object_description_format: {
        require_customer_project: false,
        require_hull_yard: false,
        require_charge: false,
        hull_yard_default: 'Ukjent/Unknown'
      }
    }
  }
}

/**
 * Creates or updates NDT settings for a company and method
 */
export async function upsertCompanyNDTSettings(
  companyId: string,
  ndtMethod: NDTMethod,
  settings: CompanyNDTSettingsUpdate
): Promise<CompanyNDTSettings | null> {
  try {
    const { data, error } = await supabase
      .from('company_ndt_settings')
      .upsert(
        {
          company_id: companyId,
          ndt_method: ndtMethod,
          ...settings
        },
        {
          onConflict: 'company_id,ndt_method'
        }
      )
      .select()
      .single()

    if (error) {
      console.error('Error upserting company NDT settings:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Failed to upsert company NDT settings:', error)
    return null
  }
}

/**
 * Formats object description based on company settings
 * Used primarily for MT reports but can be used by other methods
 */
export function formatObjectDescription(
  objectDescription: string,
  customerProject?: string,
  hullYard?: string,
  charge?: string,
  settings?: CompanyNDTSettings | CompanyNDTSettingsDetailed | null
): string {
  if (!settings || !settings.custom_fields?.object_description_format) {
    return objectDescription
  }

  const format = settings.custom_fields.object_description_format
  const parts: string[] = [objectDescription]

  // Add customer project if required
  if (format.require_customer_project && customerProject) {
    parts.push(`Customer Project: ${customerProject}`)
  }

  // Add hull/yard if required
  if (format.require_hull_yard) {
    const hullYardValue = hullYard || format.hull_yard_default || 'Ukjent/Unknown'
    parts.push(`Hull/Yard: ${hullYardValue}`)
  }

  // Add charge if required
  if (format.require_charge && charge) {
    parts.push(`Charge: ${charge}`)
  }

  return parts.join('\n')
}

/**
 * Helper function to get procedure and acceptance display values
 */
export async function getNDTSettingsDisplayValues(
  companyId: string | undefined,
  ndtMethod: NDTMethod
): Promise<{
  procedureName: string
  acceptanceCriteria: string
  referenceStandards: string
}> {
  if (companyId) {
    const settings = await getCompanyNDTSettings(companyId, ndtMethod)
    if (settings && settings.default_procedure_name) {
      return {
        procedureName: settings.default_procedure_name,
        acceptanceCriteria: settings.default_acceptance_criteria || '---',
        referenceStandards: settings.default_reference_standards
      }
    }
  }

  // Fall back to defaults
  const defaultProcedure = await getDefaultNDTTestProcedure(ndtMethod)
  const defaultAcceptance = await getDefaultNDTTestAcceptance(ndtMethod, defaultProcedure?.id)

  return {
    procedureName: defaultProcedure?.procedure_name || '---',
    acceptanceCriteria: defaultAcceptance?.acceptance_criteria || '---',
    referenceStandards: '---'
  }
}

